import { useState } from 'react';
import { CalendarClock, FlaskConical, Link2, Mail, Send, TriangleAlert } from 'lucide-react';
import { invitePreviewUrl } from '@peles/shared';
import { ShareLinkButton } from '../../common';
import { useDialog } from '../../../context/DialogContext';
import { useCan } from '../../../hooks/useCan';
import {
  isGoingOut,
  useCampaignProgress,
  useCampaignRecipientPage,
  useScheduleInviteCampaign,
  useSendInviteCampaign,
  useSetCampaignPublicLink,
  useTestSendInviteCampaign,
  useUpdateInviteCampaign,
} from '../../../hooks/useInvites';
import { CampaignSendProgress } from './CampaignSendProgress';
import { withSettings } from './useStepDraft';
import type { CampaignStepProps } from './CampaignDetailsStep';

/** A `datetime-local` value for a moment, in the viewer's own time. */
export function toLocalInputValue(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatWhen(value: Date | string): string {
  return new Date(value).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const card = 'rounded-2xl border border-gray-200 p-4';
const secondary =
  'inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50';

/**
 * Step 5: who it goes to and who it cannot reach, a test to yourself, then
 * send now or at a time — and, while it goes out, how far it has got. The
 * shared link lives here too, since it is another way of sending it.
 */
export function CampaignSendStep({ campaign, onNext }: CampaignStepProps) {
  const { alert, confirm } = useDialog();
  const canSend = useCan('messages.guests');
  const goingOut = isGoingOut(campaign);
  const { data: summary } = useCampaignRecipientPage(
    campaign.id,
    { page: 1, pageSize: 1 },
    { poll: goingOut },
  );
  const [showMissing, setShowMissing] = useState(false);
  const { data: missing } = useCampaignRecipientPage(showMissing ? campaign.id : undefined, {
    page: 1,
    pageSize: 50,
    noAddress: true,
  });
  const { data: progress } = useCampaignProgress(campaign.id, goingOut);
  const send = useSendInviteCampaign();
  const testSend = useTestSendInviteCampaign();
  const schedule = useScheduleInviteCampaign();
  const setPublicLink = useSetCampaignPublicLink();
  const updateCampaign = useUpdateInviteCampaign();
  const [when, setWhen] = useState('');

  const counts = summary?.counts;
  const toSend = (counts?.not_sent ?? 0) + (counts?.failed ?? 0);
  const noAddress = summary?.noAddress ?? 0;
  const scheduledLater =
    campaign.status === 'SCHEDULED' &&
    !!campaign.scheduledAt &&
    new Date(campaign.scheduledAt).getTime() > Date.now();
  const sent = campaign.status === 'SENT';

  const fail = async (err: unknown, fallback: string) =>
    alert({ message: err instanceof Error ? err.message : fallback, variant: 'error' });

  const handleTest = async () => {
    try {
      const result = await testSend.mutateAsync(campaign.id);
      await alert({ message: `A test is on its way to ${result.to}.`, variant: 'success' });
    } catch (err) {
      await fail(err, 'The test could not be sent');
    }
  };

  const handleSend = async () => {
    const ok = await confirm({
      message: `Send the invitation to ${toSend} guest${toSend === 1 ? '' : 's'} by email now?`,
      confirmLabel: 'Send now',
    });
    if (!ok) return;
    try {
      await send.mutateAsync(campaign.id);
    } catch (err) {
      await fail(err, 'The invitation could not be sent');
    }
  };

  const handleSchedule = async () => {
    const at = when ? new Date(when) : null;
    if (!at || Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) {
      await alert({ message: 'Choose a time in the future.', variant: 'warning' });
      return;
    }
    try {
      await schedule.mutateAsync({ campaignId: campaign.id, scheduledAt: at.toISOString() });
      setWhen('');
    } catch (err) {
      await fail(err, 'Could not schedule it');
    }
  };

  const handleUnschedule = async () => {
    try {
      await schedule.mutateAsync({ campaignId: campaign.id, scheduledAt: null });
    } catch (err) {
      await fail(err, 'Could not cancel the schedule');
    }
  };

  const handlePublicLink = async (enabled: boolean) => {
    if (!enabled) {
      const ok = await confirm({
        message: 'Turn off the shared link? Anyone still holding it will get a "not found" page.',
        variant: 'danger',
        confirmLabel: 'Turn off',
      });
      if (!ok) return;
    }
    try {
      await setPublicLink.mutateAsync({ campaignId: campaign.id, enabled });
    } catch (err) {
      await fail(err, 'Could not change the shared link');
    }
  };

  const setAllowPublicRsvp = async (allow: boolean) => {
    try {
      await updateCampaign.mutateAsync({
        id: campaign.id,
        updates: { settings: withSettings(campaign, { allowPublicRsvp: allow }) },
      });
    } catch (err) {
      await fail(err, 'Could not save that');
    }
  };

  const rsvpEnabled = campaign.settings?.rsvpEnabled !== false;
  const publicUrl = campaign.publicToken
    ? invitePreviewUrl(window.location.origin, campaign.publicToken)
    : null;

  return (
    <div className="space-y-4">
      <section className={card}>
        <h3 className="text-sm font-semibold text-gray-900">Who it goes to</h3>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-gray-500">On the campaign</dt>
            <dd className="text-lg font-semibold text-gray-900">{counts?.all ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">To send now</dt>
            <dd className="text-lg font-semibold text-gray-900">{summary ? toSend : '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Channel</dt>
            <dd className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
              <Mail className="h-4 w-4" aria-hidden /> Email
            </dd>
          </div>
        </dl>
        {noAddress > 0 && (
          <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
            <p className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                {noAddress} guest{noAddress === 1 ? ' has' : 's have'} no email address, so the
                email cannot reach them. Send them their link another way from the Guests step, or
                add an address on the guest list.
              </span>
            </p>
            <button
              type="button"
              onClick={() => setShowMissing((v) => !v)}
              className="mt-1 text-xs font-medium underline"
              aria-expanded={showMissing}
            >
              {showMissing ? 'Hide who' : 'Show who'}
            </button>
            {showMissing && missing && (
              <ul className="mt-2 list-disc pl-5 text-xs">
                {missing.items.map((r) => (
                  <li key={r.id}>{r.guestName ?? 'Unnamed guest'}</li>
                ))}
                {missing.total > missing.items.length && (
                  <li>and {missing.total - missing.items.length} more</li>
                )}
              </ul>
            )}
          </div>
        )}
      </section>

      {canSend && (
        <section className={`${card} space-y-4`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Send</h3>
            <button
              type="button"
              onClick={() => void handleTest()}
              disabled={testSend.isPending}
              className={secondary}
            >
              <FlaskConical className="h-4 w-4" aria-hidden />
              {testSend.isPending ? 'Sending a test…' : 'Send a test to me'}
            </button>
          </div>

          {goingOut ? (
            <CampaignSendProgress progress={progress} active />
          ) : (
            <>
              {sent && (
                <p className="text-sm text-gray-700">
                  Sent{campaign.sentAt ? ` ${formatWhen(campaign.sentAt)}` : ''}
                  {campaign.sentByName ? ` by ${campaign.sentByName}` : ''}.
                  {toSend > 0 &&
                    ` ${toSend} guest${toSend === 1 ? '' : 's'} added since, or not reached, can be sent now.`}
                </p>
              )}
              {scheduledLater && campaign.scheduledAt && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl bg-blue-50 p-3 text-sm text-blue-800">
                  <CalendarClock className="h-4 w-4" aria-hidden />
                  <span>Goes out {formatWhen(campaign.scheduledAt)}.</span>
                  <button
                    type="button"
                    onClick={() => void handleUnschedule()}
                    className="ml-auto text-xs font-medium underline"
                  >
                    Cancel the schedule
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <button
                  type="button"
                  onClick={() => void handleSend()}
                  disabled={toSend === 0 || send.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" aria-hidden />
                  {toSend === 0 ? 'Nobody to send to' : `Send now to ${toSend}`}
                </button>
                {!sent && (
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label htmlFor="campaign-send-at" className="block text-xs text-gray-600">
                        Or send at
                      </label>
                      <input
                        id="campaign-send-at"
                        type="datetime-local"
                        value={when}
                        min={toLocalInputValue(new Date())}
                        onChange={(e) => setWhen(e.target.value)}
                        className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleSchedule()}
                      disabled={!when || schedule.isPending}
                      className={secondary}
                    >
                      <CalendarClock className="h-4 w-4" aria-hidden />
                      Schedule
                    </button>
                  </div>
                )}
              </div>
              {sent && progress && <CampaignSendProgress progress={progress} active={false} />}
            </>
          )}
        </section>
      )}

      <section className={`${card} space-y-2`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Shared link</h3>
          <button
            type="button"
            onClick={() => void handlePublicLink(!campaign.publicToken)}
            className={secondary}
          >
            <Link2 className="h-4 w-4" aria-hidden />
            {campaign.publicToken ? 'Turn off' : 'Create a link'}
          </button>
        </div>
        <p className="text-xs text-gray-500">
          One link for everybody, with no guest's name on it — for a WhatsApp group or a post. It
          previews with the event's name, date and logo.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={rsvpEnabled && campaign.settings?.allowPublicRsvp !== false}
            disabled={!rsvpEnabled}
            onChange={(e) => void setAllowPublicRsvp(e.target.checked)}
          />
          People opening it may answer, joining the guest list
        </label>
        {publicUrl && (
          <>
            <ShareLinkButton
              url={publicUrl}
              text={campaign.subject || campaign.name}
              title={campaign.name}
            />
            <p className="break-all text-xs text-gray-400">{publicUrl}</p>
          </>
        )}
      </section>

      {onNext && sent && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onNext}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            See the results
          </button>
        </div>
      )}
    </div>
  );
}
